/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import {
    Sparkles, AlertTriangle, TrendingUp, Award, Clock, Target,
    ArrowRight, Activity,
} from 'lucide-react';
import api from '../../lib/api';
import AIInsightCard from '../ui/AIInsightCard';
import AnimatedCounter from '../ui/AnimatedCounter';
import SparklineChart from '../ui/SparklineChart';

/**
 * AI Weekly Synthesis V2 — Homly "Control Room" design.
 *
 * Renders:
 *  - Briefing IA header (priorities count + exposed pipeline)
 *  - Health Score hero (0-100, deterministic) + 4 KPI tiles with sparklines
 *  - 3 Bento columns: Urgences · Opportunités · Wins
 *  - Footer signature "Gemini 2.5 Flash · Mise à jour il y a X min"
 */
export default function AIWeeklySynthesisV2() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            try {
                setLoading(true);
                const res = await api.get('/cockpit-ai/weekly-synthesis');
                if (!cancelled) {
                    setData(res.data);
                    setError(null);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.response?.data?.detail || 'Erreur de chargement');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchData();
        return () => {
            cancelled = true;
        };
    }, []);

    const relativeTime = useMemo(() => {
        if (!data?.generated_at) return '';
        try {
            const diffMin = Math.max(1, Math.round((Date.now() - new Date(data.generated_at).getTime()) / 60000));
            if (diffMin < 60) return `il y a ${diffMin} min`;
            const h = Math.round(diffMin / 60);
            return `il y a ${h} h`;
        } catch { return ''; }
    }, [data]);

    if (loading) {
        return <SynthesisSkeleton />;
    }

    if (error || !data) {
        return (
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5 text-center text-amber-300 text-sm" data-testid="ai-synthesis-error">
                {error || 'Synthèse indisponible — recharger la page.'}
            </div>
        );
    }

    const { health, briefing, kpis, insights } = data;
    const scoreColor =
        health.score >= 75 ? '#10B981' :
        health.score >= 50 ? '#FF914D' : '#EF4444';

    const allCategoriesEmpty = !insights.urgent?.length && !insights.opportunity?.length && !insights.win?.length;

    return (
        <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6"
            data-testid="ai-weekly-synthesis-v2"
            style={{ fontFamily: 'IBM Plex Sans, Manrope, sans-serif' }}
        >
            {/* ─── BRIEFING IA HEADER ─── */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="relative overflow-hidden rounded-2xl p-5 border border-foreground/10"
                style={{
                    background: 'linear-gradient(135deg, rgba(104,23,147,0.15) 0%, rgba(255,145,77,0.08) 100%)',
                }}
                data-testid="ai-briefing-header"
            >
                <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shrink-0">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <p
                                className="text-[10px] uppercase tracking-widest text-foreground/40 mb-1"
                                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                            >
                                Briefing du jour · Pilote · IA Homly
                            </p>
                            <h2
                                className="text-lg md:text-xl font-semibold text-foreground leading-tight tracking-tight"
                                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                            >
                                Bonjour {data.manager_first_name}.{' '}
                                <span style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }} className="text-[#FF914D]">
                                    {briefing.priorities_count} priorité{briefing.priorities_count > 1 ? 's' : ''}
                                </span>{' '}
                                identifiée{briefing.priorities_count > 1 ? 's' : ''}{briefing.exposed_amount > 0 ? ' · ' : ''}
                                {briefing.exposed_amount > 0 && (
                                    <>
                                        <span style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }} className="text-foreground">
                                            <AnimatedCounter value={briefing.exposed_amount} suffix=" €" />
                                        </span>{' '}
                                        <span className="text-foreground/50 text-sm font-normal">
                                            {briefing.exposed_label}
                                        </span>
                                    </>
                                )}
                            </h2>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* ─── HEALTH SCORE HERO + KPI TILES ─── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Big health score */}
                <div
                    className="md:col-span-2 relative overflow-hidden p-7 rounded-2xl border border-foreground/10 bg-foreground/[0.02] backdrop-blur-xl"
                    data-testid="ai-weekly-health-score"
                >
                    <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-30"
                        style={{ background: scoreColor }} />
                    <p
                        className="text-[10px] uppercase tracking-widest text-foreground/40 mb-3"
                        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                    >
                        Score santé agence · {data.week_label}
                    </p>
                    <div className="flex items-end gap-5">
                        <div className="flex items-baseline gap-1">
                            <span
                                className="text-6xl md:text-7xl font-medium tracking-tighter text-foreground"
                                style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                            >
                                <AnimatedCounter value={health.score} duration={1800} />
                            </span>
                            <span
                                className="text-2xl text-foreground/30 font-medium"
                                style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                            >
                                /100
                            </span>
                        </div>
                        <div className="pb-3 flex flex-col gap-1">
                            <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border w-fit"
                                style={{
                                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                                    color: scoreColor,
                                    backgroundColor: `${scoreColor}1a`,
                                    borderColor: `${scoreColor}55`,
                                }}
                            >
                                {health.label}
                            </span>
                            {health.delta !== 0 && (
                                <span
                                    className="text-[10px] text-foreground/40"
                                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                                >
                                    {health.delta > 0 ? '+' : ''}{health.delta} pts vs S-1
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="mt-5 flex items-center gap-3">
                        <div className="flex-1 h-1.5 rounded-full bg-foreground/5 overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${health.score}%` }}
                                transition={{ duration: 1.6, ease: 'easeOut' }}
                                className="h-full rounded-full"
                                style={{ background: `linear-gradient(90deg, #681793, ${scoreColor})` }}
                            />
                        </div>
                        <span
                            className="text-[10px] text-foreground/30"
                            style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                        >
                            Top 20 % ≥ 85
                        </span>
                    </div>
                </div>

                {/* Quick KPIs 2x2 */}
                <div className="grid grid-cols-2 gap-3">
                    <KpiTile
                        label="CA encaissé / mois"
                        value={kpis.ca_month}
                        suffix=" €"
                        color="#10B981"
                        compact
                        sparkline={kpis.sparklines?.ca_month || []}
                    />
                    <KpiTile
                        label="Pipeline pondéré"
                        value={kpis.pipeline_pondere}
                        suffix=" €"
                        color="#A855F7"
                        compact
                        sparkline={kpis.sparklines?.pipeline_pondere || []}
                    />
                    <KpiTile
                        label="Mandats actifs"
                        value={kpis.active_mandates}
                        suffix=""
                        color="#FF914D"
                        sparkline={kpis.sparklines?.active_mandates || []}
                    />
                    <KpiTile
                        label="Ventes / mois"
                        value={kpis.sold_this_month}
                        suffix=""
                        color="#3B82F6"
                        sparkline={kpis.sparklines?.sold_this_month || []}
                    />
                </div>
            </div>

            {/* ─── 3-COLUMN BENTO INSIGHTS ─── */}
            <section className="space-y-3">
                <div className="flex items-center justify-between">
                    <h3
                        className="text-lg md:text-xl font-semibold tracking-tight text-foreground"
                        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                    >
                        Cette semaine, focus sur les axes prioritaires
                    </h3>
                    <span
                        className="text-[10px] text-foreground/40 hidden sm:block"
                        style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                    >
                        Insights classés par impact €
                    </span>
                </div>

                {allCategoriesEmpty ? (
                    <div className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-6 text-center text-foreground/40 text-sm">
                        Créez vos premiers mandats pour activer l'analyse hebdomadaire.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <InsightColumn
                            title="Urgences"
                            count={insights.urgent.length}
                            color="#FF914D"
                            icon={AlertTriangle}
                            subtitle="À traiter sous 48 h"
                            items={insights.urgent}
                            category="urgent"
                            onCta={(link) => link && navigate(link)}
                            colKey="urgent"
                        />
                        <InsightColumn
                            title="Opportunités"
                            count={insights.opportunity.length}
                            color="#A855F7"
                            icon={Target}
                            subtitle="Levier de croissance"
                            items={insights.opportunity}
                            category="opportunity"
                            onCta={(link) => link && navigate(link)}
                            colKey="opportunity"
                        />
                        <InsightColumn
                            title="Wins"
                            count={insights.win.length}
                            color="#10B981"
                            icon={Award}
                            subtitle="À célébrer en réunion"
                            items={insights.win}
                            category="win"
                            onCta={(link) => link && navigate(link)}
                            colKey="win"
                        />
                    </div>
                )}
            </section>

            {/* ─── SIGNATURE FOOTER ─── */}
            <footer className="flex flex-col md:flex-row items-center justify-between gap-3 pt-5 border-t border-foreground/5">
                <div className="flex items-center gap-3 text-[11px] text-foreground/40 flex-wrap">
                    <div className="flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-[#FF914D]" />
                        <span style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            Analyse propulsée par{' '}
                            <span className="text-foreground/70 font-semibold">
                                Pilote · IA Homly
                            </span>
                        </span>
                    </div>
                    <span className="w-1 h-1 rounded-full bg-foreground/20" />
                    <span style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
                        <Clock className="w-3 h-3 inline mr-1" />
                        Mise à jour {relativeTime}
                    </span>
                </div>
                <Link
                    to="/admin/ai-report"
                    className="text-xs font-semibold text-[#FF914D] hover:text-[#FF914D]/80 transition-colors flex items-center gap-1"
                    data-testid="cockpit-view-full-report"
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                    Voir le rapport complet
                    <ArrowRight className="w-3 h-3" />
                </Link>
            </footer>
        </motion.section>
    );
}

/* ──────────── Sub-components ──────────── */

function InsightColumn({ title, count, color, icon: Icon, subtitle, items, category, onCta, colKey }) {
    return (
        <div className="space-y-3" data-testid={`ai-insight-column-${category}`}>
            <div className="flex items-center justify-between pb-2 border-b border-foreground/5">
                <div className="flex items-center gap-2">
                    <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center"
                        style={{ background: `${color}1a`, border: `1px solid ${color}33` }}
                    >
                        <Icon className="w-3.5 h-3.5" style={{ color }} />
                    </div>
                    <div>
                        <p
                            className="text-sm font-semibold text-foreground tracking-tight"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                        >
                            {title}
                            <span
                                className="ml-2 text-xs text-foreground/30"
                                style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                            >
                                · {count}
                            </span>
                        </p>
                        <p
                            className="text-[10px] uppercase tracking-widest text-foreground/30"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                        >
                            {subtitle}
                        </p>
                    </div>
                </div>
            </div>

            {items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-foreground/5 p-4 text-center text-[11px] text-foreground/30">
                    {category === 'win' ? 'Pas de win cette semaine.' : 'Aucun élément.'}
                </div>
            ) : (
                items.map((insight, i) => {
                    const IconComp = LucideIcons[insight.icon] || Activity;
                    return (
                        <AIInsightCard
                            key={`${colKey}-${i}-${insight.headline}`}
                            category={insight.category}
                            icon={IconComp}
                            headline={insight.headline}
                            metricValue={insight.metric_value}
                            metricPrefix={insight.metric_prefix}
                            metricSuffix={insight.metric_suffix}
                            why={insight.why}
                            ctaLabel={insight.cta_label}
                            sparkline={insight.sparkline || []}
                            testId={`ai-insight-card-${category}-${i}`}
                            delay={0.1 * i}
                            onCta={() => onCta(insight.cta_link)}
                        />
                    );
                })
            )}
        </div>
    );
}

function KpiTile({ label, value, suffix, color, sparkline, compact }) {
    return (
        <div className="p-3 rounded-xl border border-foreground/10 bg-foreground/[0.02] hover:bg-foreground/[0.04] transition-colors min-h-[88px] flex flex-col justify-between">
            <p
                className="text-[10px] uppercase tracking-widest text-foreground/40 mb-1"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
                {label}
            </p>
            <div className="flex items-baseline gap-1">
                <span
                    className="text-xl font-medium tabular-nums"
                    style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', color }}
                >
                    <AnimatedCounter value={value} format={compact ? 'compact' : 'fr'} />
                </span>
                <span
                    className="text-xs opacity-60"
                    style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', color }}
                >
                    {suffix}
                </span>
            </div>
            <div className="mt-1">
                <SparklineChart
                    data={sparkline && sparkline.length > 0 ? sparkline : [0, 0, 0]}
                    color={color}
                    width={120}
                    height={18}
                    showDot={false}
                />
            </div>
        </div>
    );
}

function SynthesisSkeleton() {
    return (
        <div className="space-y-5" data-testid="ai-synthesis-skeleton">
            <div className="h-20 rounded-2xl bg-foreground/[0.02] border border-foreground/10 animate-pulse" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 h-44 rounded-2xl bg-foreground/[0.02] border border-foreground/10 animate-pulse" />
                <div className="grid grid-cols-2 gap-3">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={`sk-kpi-${i}`} className="h-20 rounded-xl bg-foreground/[0.02] border border-foreground/10 animate-pulse" />
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                    <div key={`sk-col-${i}`} className="h-72 rounded-2xl bg-foreground/[0.02] border border-foreground/10 animate-pulse" />
                ))}
            </div>
        </div>
    );
}
